import React, { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { httpService } from "../../services/httpService";
import { computeFixedDropdownPosition, fixedDropdownStyle } from "./dropdownPositionUtils";

// Dynamic type configurations for inline rendering
const TYPE_CONFIGS = {
  yes_no: {
    label: "Yes/No",
    component: ({ value, onChange }) => (
      <span className="inline-flex gap-1 mx-1 align-middle">
        {["Yes", "No"].map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onChange(opt);
            }}
            className={`px-2 py-0.5 text-[10px] font-semibold rounded border transition-all inline-flex ${
              value === opt
                ? opt === "Yes"
                  ? "bg-emerald-600 border-emerald-600 text-white"
                  : "bg-rose-600 border-rose-600 text-white"
                : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
            }`}
          >
            {opt}
          </button>
        ))}
      </span>
    ),
  },
  text: {
    label: "Text",
    component: ({ value, onChange, placeholder }) => (
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || "Enter..."}
        className="text-[10px] border border-slate-200 rounded px-2 py-0.5 w-24 inline-block mx-1 align-middle focus:outline-none focus:ring-1 focus:ring-blue-400"
        onClick={(e) => e.stopPropagation()}
      />
    ),
  },
  scale: {
    label: "Scale",
    component: ({ value, onChange, min = 0, max = 10 }) => {
      const nums = Array.from({ length: max - min + 1 }, (_, i) => min + i);
      return (
        <span className="inline-flex gap-0.5 mx-1 align-middle">
          {nums.map((n) => (
            <button
              key={n}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange(String(n));
              }}
              className={`w-5 h-5 text-[9px] font-medium rounded border transition-all ${
                value === String(n)
                  ? "bg-blue-600 border-blue-600 text-white"
                  : "bg-white border-slate-200 text-slate-600 hover:border-blue-300"
              }`}
            >
              {n}
            </button>
          ))}
        </span>
      );
    },
  },
  // ⭐ NEW — freeform observation, not a question the vet is asking themselves.
  // The vet is just jotting down something the owner said. Kept here mainly so
  // it shows up as a 4th "Add as:" button and so any legacy/preset entry with
  // this answerType still has a sane fallback renderer.
  note: {
    label: "Note",
    component: ({ value }) => (
      <span className="text-[11px] text-slate-500 italic ml-1 align-middle">
        {value}
      </span>
    ),
  },
};

// ✅ Pre-compute entries for the type configs
const TYPE_CONFIG_ENTRIES = Object.entries(TYPE_CONFIGS);

const SOAPQuestionField = ({
  section,
  label,
  structuredEntries = [],
  onEntriesChange,
  customActions,
  textareaRows = 5,
}) => {
  const [presetQuestions, setPresetQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0, flipUp: false });
  const [notes, setNotes] = useState("");
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // ✅ Use useMemo to compute usedQuestions - prevents recreation on every render
  // ⭐ CHANGED — notes have an empty `question` by design (see handleAddCustom),
  // so we exclude blank questions from the dedupe set. Otherwise a second note
  // would be silently blocked as "already used" once the first empty-question
  // note exists.
  const usedQuestions = useMemo(() => {
    return new Set(
      structuredEntries
        .filter((e) => e.question)
        .map((e) => e.question.toLowerCase())
    );
  }, [structuredEntries]);

  const fetchQuestions = useCallback(async () => {
    setLoadingQuestions(true);
    try {
      const res = await httpService.get(`/vendorpet/getsoapquestions?section=${section}`);
      const list = res?.data || [];
      setPresetQuestions(list);
    } catch (err) {
      console.error("Failed to load SOAP questions:", err);
    } finally {
      setLoadingQuestions(false);
    }
  }, [section]);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  // ✅ Update suggestions when input changes
  useEffect(() => {
    if (!inputValue.trim()) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    const matched = presetQuestions.filter(
      (q) =>
        !usedQuestions.has(q.question.toLowerCase()) &&
        q.question.toLowerCase().includes(inputValue.toLowerCase())
    );
    setSuggestions(matched);
    setShowDropdown(true);
    
    if (inputRef.current) {
      setDropdownPosition(computeFixedDropdownPosition(inputRef.current));
    }
  }, [inputValue, presetQuestions, usedQuestions]);

  // ✅ Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ✅ Update dropdown position on scroll/resize
  useEffect(() => {
    if (!showDropdown) return;
    
    const updatePosition = () => {
      if (inputRef.current) {
        setDropdownPosition(computeFixedDropdownPosition(inputRef.current));
      }
    };
    
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [showDropdown]);

  const addEntry = (q) => {
    if (!q?.question?.trim() || usedQuestions.has(q.question.toLowerCase())) return;
    onEntriesChange([
      ...structuredEntries,
      {
        id: Date.now(),
        question: q.question.trim(),
        answer: "",
        answerType: q.answerType || "yes_no",
        options: q.options || [],
        isCustom: q.isCustom || false,
      },
    ]);
    setInputValue("");
    setShowDropdown(false);
  };

  const updateAnswer = (id, answer) => {
    onEntriesChange(structuredEntries.map((e) => 
      e.id === id ? { ...e, answer } : e
    ));
  };

  const removeEntry = (id) => {
    onEntriesChange(structuredEntries.filter((e) => e.id !== id));
  };

  const handleAddCustom = async (type) => {
    const questionText = inputValue.trim();
    if (!questionText) return;

    // ⭐ NEW — "Note" isn't a question+answer pair, it's just a freeform
    // observation. Whatever the vet typed becomes the note content itself
    // (question left blank), and we deliberately skip saving it to the
    // reusable preset-question library below — a one-off note about this
    // pet isn't a template other vets/visits should see suggested.
    if (type === "note") {
      onEntriesChange([
        ...structuredEntries,
        {
          id: Date.now(),
          question: "",
          answer: questionText,
          answerType: "note",
          options: [],
          isCustom: true,
        },
      ]);
      setInputValue("");
      setShowDropdown(false);
      return;
    }

    addEntry({
      question: questionText,
      answerType: type,
      options: type === "select" ? ["Option 1", "Option 2"] : [],
      isCustom: true,
    });

    try {
      await httpService.post("/vendorpet/addsoapquestion", {
        section,
        question: questionText,
        answerType: type,
        options: type === "select" ? ["Option 1", "Option 2"] : [],
      });
    } catch (err) {
      console.error(err);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && inputValue.trim()) {
      e.preventDefault();
      handleAddCustom("yes_no");
    }
  };

  const renderInlineEntry = (entry) => {
    // ⭐ NEW — notes render as a plain editable line tagged "NOTE", with no
    // question label or answer-type widget, since there's no question here.
    if (entry.answerType === "note") {
      return (
        <div key={entry.id || `note-${entry.answer}`} className="flex items-start gap-1.5 mb-2">
          <span className="text-black text-lg leading-5 mt-0.5">•</span>
          <div className="flex-1 flex items-center flex-wrap gap-1.5">
            {/* <span className="text-[9px] font-bold text-amber-600 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 uppercase tracking-wide shrink-0">
              Note
            </span> */}
            <input
              type="text"
              value={entry.answer || ""}
              onChange={(e) => updateAnswer(entry.id, e.target.value)}
              placeholder="What did the owner say..."
              className="flex-1 min-w-[120px] text-[12px] text-slate-700 bg-transparent border-none focus:outline-none focus:ring-0 px-0"
            />
          </div>
          <button
            type="button"
            onClick={() => removeEntry(entry.id)}
            className="p-0.5 text-slate-400 hover:text-red-500 transition-colors shrink-0"
          >
            <XMarkIcon className="w-3.5 h-3.5" />
          </button>
        </div>
      );
    }

    const typeConfig = TYPE_CONFIGS[entry.answerType] || TYPE_CONFIGS.yes_no;
    const Component = typeConfig.component;
    
    return (
      <div key={entry.id || `entry-${entry.question}`} className="flex items-start gap-1.5 mb-2">
        <span className="text-black text-lg leading-5 mt-0.5">•</span>
        <div className="flex-1 flex items-center flex-wrap gap-1">
          <span className="text-[12px] font-medium text-slate-700">
            {entry.question}
          </span>
          <Component
            value={entry.answer}
            onChange={(val) => updateAnswer(entry.id, val)}
            options={entry.options || []}
          />
        </div>
        <button
          type="button"
          onClick={() => removeEntry(entry.id)}
          className="p-0.5 text-slate-400 hover:text-red-500 transition-colors shrink-0"
        >
          <XMarkIcon className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  };

  return (
    <div className="relative">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
          {label}
        </span>
        {customActions && <div className="flex items-center gap-2">{customActions}</div>}
      </div>

      {/* Question search/add field */}
      <div className="mb-3">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onFocus={() => {
            if (inputValue.trim()) {
              setShowDropdown(true);
              if (inputRef.current) {
                setDropdownPosition(computeFixedDropdownPosition(inputRef.current));
              }
            }
          }}
          onKeyPress={handleKeyPress}
          placeholder="Type to search or add new question..."
          className="w-full text-[13px] text-slate-800 bg-white border border-slate-200 rounded-lg px-3 py-2.5 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 placeholder-slate-300"
        />
      </div>

      {/* Dropdown Portal */}
      {showDropdown && createPortal(
        <div
          ref={dropdownRef}
          className="fixed bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto z-[9999]"
          style={fixedDropdownStyle(dropdownPosition)}
        >
          {inputValue.trim() && (
            <div className="px-3 py-2 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50 sticky top-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-medium text-gray-600 flex items-center gap-1">
                  <PlusIcon className="w-3.5 h-3.5 text-blue-500" />
                  Add "{inputValue.trim()}" as:
                </span>
                <div className="flex gap-1 flex-wrap">
                  {TYPE_CONFIG_ENTRIES.map(([key, config]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => handleAddCustom(key)}
                      className="text-[10px] px-2 py-1 rounded-md border font-medium bg-white border-gray-200 text-gray-600 hover:border-blue-300 hover:bg-blue-50"
                    >
                      {config.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {loadingQuestions ? (
            <p className="text-xs text-gray-400 text-center py-4">Loading...</p>
          ) : suggestions.length > 0 ? (
            <div className="py-1">
              {suggestions.map((q) => (
                <button
                  key={q._id || q.id || `question-${q.question}`}
                  type="button"
                  onClick={() => addEntry(q)}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-blue-50"
                >
                  <span className="text-sm text-gray-700 flex-1">{q.question}</span>
                  <span className="text-[9px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                    {TYPE_CONFIGS[q.answerType]?.label || "Yes/No"}
                  </span>
                </button>
              ))}
            </div>
          ) : inputValue.trim() && (
            <div className="p-3 text-center">
              <p className="text-xs text-gray-400">No existing questions match.</p>
            </div>
          )}
        </div>,
        document.body
      )}

      {/* Textarea-like container with bullet points */}
      <div className="mt-3">
        {/* Display structured entries below the textarea */}
        {structuredEntries.length > 0 && (
          <div className="mt-3 pt-3 border-t border-slate-200">
            {structuredEntries.map(entry => renderInlineEntry(entry))}
          </div>
        )}
        {/* <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Describe presenting complaint, owner's concern, duration of issue, and any other relevant observations..."
          className="w-full text-[13px] text-slate-700 border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-transparent bg-white leading-relaxed resize-y"
          style={{ minHeight: `${textareaRows * 12}px` }}
        /> */}
      </div>
    </div>
  );
};

export default SOAPQuestionField;